/* eslint-disable camelcase */
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import { SectionCard, DataTable, Drawer, Chip, FieldText } from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Redux
import { useSelector, useDispatch } from "react-redux";
import { openNotificationPopup } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { selectAllMeters, useGetMetersByCommunityIdQuery } from "state/meters/metersApiSlice";
import {
  selectAllResources,
  useGetResourcesByCommunityIdQuery,
} from "state/resources/resourcesApiSlice";
import {
  useGetMeterStaticParametersByMeterIdQuery,
  useUpdateMeterStaticParameterMutation,
  useAddMeterStaticParameterMutation,
} from "state/meterParameters/meterStaticParametersApiSlice";

const PARAM_FIELDS = [
  { key: "l_gic", label: "lGic" },
  { key: "l_bic", label: "lBic" },
  { key: "l_cont", label: "lCont" },
  { key: "p_meter_max", label: "pMeterMax" },
  { key: "e_bn_min", label: "eBnMin" },
  { key: "e_bn_max", label: "eBnMax" },
  { key: "p_gn_min", label: "pGnMin" },
  { key: "p_gn_max", label: "pGnMax" },
];

const EMPTY_PARAMS = {
  l_cont: null,
  l_bic: null,
  l_gic: null,
  e_bn_min: null,
  e_bn_max: null,
  p_meter_max: null,
  p_gn_min: null,
  p_gn_max: null,
};

function ListMeters() {
  const dispatch = useDispatch();
  const { t } = useTranslation(["sizing"]);
  const community = useSelector(selectCurrentCommunity);

  const { meters } = useGetMetersByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data }) => ({ meters: data && selectAllMeters(data) }),
    }
  );

  const { resources } = useGetResourcesByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data: resourcesList }) => ({
        resources: resourcesList && selectAllResources(resourcesList),
      }),
    }
  );

  const cpeMeters = useMemo(() => (meters || []).filter((meter) => meter.type === "CPE"), [meters]);

  const [open, setOpen] = useState(false);
  const [selectedMeter, setSelectedMeter] = useState(null);
  const [staticParameters, setStaticParameters] = useState(EMPTY_PARAMS);

  const {
    data: fetchedStaticParameters,
    isFetching: isFetchingStaticParameters,
    refetch: refetchStaticParameters,
  } = useGetMeterStaticParametersByMeterIdQuery(
    {
      communityId: community.id,
      meterId: selectedMeter?.id,
    },
    {
      skip: !selectedMeter,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    }
  );

  useEffect(() => {
    if (selectedMeter && fetchedStaticParameters) {
      const firstStaticParameterId = fetchedStaticParameters.ids[0];
      const staticData = fetchedStaticParameters.entities[firstStaticParameterId] || {};

      setStaticParameters({
        l_cont: parseFloat(staticData.l_cont) || null,
        l_bic: parseFloat(staticData.l_bic) || null,
        l_gic: parseFloat(staticData.l_gic) || null,
        e_bn_min: parseFloat(staticData.e_bn_min) || null,
        e_bn_max: parseFloat(staticData.e_bn_max) || null,
        p_meter_max: parseFloat(staticData.p_meter_max) || null,
        p_gn_min: parseFloat(staticData.p_gn_min) || null,
        p_gn_max: parseFloat(staticData.p_gn_max) || null,
      });
    } else {
      setStaticParameters(EMPTY_PARAMS);
    }
  }, [fetchedStaticParameters, selectedMeter]);

  const [updateMeterStaticParameter] = useUpdateMeterStaticParameterMutation();
  const [addMeterStaticParameter] = useAddMeterStaticParameterMutation();

  const handleClose = () => {
    setOpen(false);
    setSelectedMeter(null);
    setStaticParameters(EMPTY_PARAMS);
  };

  const onUpdateMeterClicked = async (meter) => {
    try {
      setSelectedMeter(meter);
      await refetchStaticParameters();
      setOpen(true);
    } catch {
      dispatch(
        openNotificationPopup({
          notification: t("meters.notifications.genericError"),
          type: "error",
        })
      );
    }
  };

  const setFieldValue = (field, value) => {
    const parsed = value === "" ? "" : parseFloat(String(value).replace(/,/g, "."));
    setStaticParameters((prev) => ({
      ...prev,
      [field]: Number.isNaN(parsed) ? "" : parsed,
    }));
  };

  const onUpdateMeterParameters = async () => {
    try {
      const updatedStaticParameters = PARAM_FIELDS.reduce((acc, { key }) => {
        const v = staticParameters[key];
        acc[key] = v !== null && v !== "" ? parseFloat(v) : 0;
        return acc;
      }, {});

      if (fetchedStaticParameters && fetchedStaticParameters.ids.length > 0) {
        const firstStaticParameterId = fetchedStaticParameters.ids[0];
        await updateMeterStaticParameter({
          communityId: community.id,
          parameterId: firstStaticParameterId,
          updatedData: updatedStaticParameters,
        }).unwrap();
      } else {
        await addMeterStaticParameter({
          communityId: community.id,
          parameterData: {
            ...updatedStaticParameters,
            meter_id: selectedMeter.id,
            community_id: community.id,
          },
        }).unwrap();
      }

      dispatch(
        openNotificationPopup({
          notification: t("meters.notifications.updateSuccess"),
          type: "success",
        })
      );
      handleClose();
    } catch (err) {
      dispatch(openNotificationPopup({ notification: err.message, type: "error" }));
      handleClose();
    }
  };

  const rows = useMemo(
    () =>
      cpeMeters.map((meter) => {
        const resourceNames = resources
          ?.filter((resource) => resource.meter_id === meter.id)
          .map((resource) => resource.name)
          .join(", ");
        return {
          id: meter.id,
          meter: meter.identifier,
          type: meter.type,
          installation_type: meter.installation_type,
          ord: meter.ord ?? "—",
          resource: resourceNames || "—",
          cpe: meter.cpe_id ? cpeMeters.find((m) => m.id === meter.cpe_id)?.identifier ?? "—" : "—",
          meterEntity: meter,
        };
      }),
    [cpeMeters, resources]
  );

  const columns = [
    {
      key: "meter",
      label: t("meters.table.columns.meter"),
      render: (v) => (
        <div className="rec-cell-member">
          <div
            className="rec-av-sm"
            style={{ background: "var(--accent-soft)", color: "var(--accent-deep)" }}
          >
            <Icon name="electric_meter" style={{ fontSize: 18 }} />
          </div>
          <span className="font-[family-name:var(--mono)] text-[12.5px] text-ink">{v}</span>
        </div>
      ),
    },
    {
      key: "type",
      label: t("meters.table.columns.type"),
      render: (v) => <Chip tone="teal">{v}</Chip>,
    },
    {
      key: "installation_type",
      label: t("meters.table.columns.installationType"),
      render: (v) => <Chip tone="gray">{v}</Chip>,
    },
    { key: "ord", label: t("meters.table.columns.ord") },
    {
      key: "resource",
      label: t("meters.table.columns.resource"),
      render: (v) =>
        v === "—" ? <span className="text-ink-3">—</span> : <span className="text-ink">{v}</span>,
    },
    { key: "cpe", label: t("meters.table.columns.cpe") },
    {
      key: "_actions",
      label: "",
      sort: false,
      align: "right",
      render: (v, row) => (
        <button
          type="button"
          className="rec-btn rec-btn-ghost rec-btn-sm"
          onClick={() => onUpdateMeterClicked(row.meterEntity)}
        >
          <Icon name="edit" />
          {t("meters.table.actions.edit")}
        </button>
      ),
    },
  ];

  return (
    <SectionCard
      id="sizing-members"
      icon="groups"
      title={t("meters.list.title")}
      sub={t("meters.list.subtitle")}
    >
      <DataTable
        columns={columns}
        rows={rows}
        searchKeys={["meter", "cpe"]}
        search={t("meters.table.search")}
      />

      <Drawer
        open={open && !isFetchingStaticParameters}
        width={560}
        title={t("meters.modal.title")}
        sub={selectedMeter?.identifier ?? ""}
        onClose={handleClose}
        footer={
          <>
            <button type="button" className="rec-btn rec-btn-ghost" onClick={handleClose}>
              {t("meters.optimizationModal.buttons.cancel")}
            </button>
            <button
              type="button"
              className="rec-btn rec-btn-primary"
              onClick={onUpdateMeterParameters}
            >
              <Icon name="check" />
              {t("meters.optimizationModal.buttons.save")}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {PARAM_FIELDS.map(({ key, label }) => (
            <FieldText
              key={key}
              label={t(`meters.modal.fields.${label}`)}
              type="number"
              value={staticParameters?.[key] ?? ""}
              onChange={(v) => setFieldValue(key, v)}
            />
          ))}
        </div>
      </Drawer>
    </SectionCard>
  );
}

export default ListMeters;
